/**
 * IR (see ir.js) → Apiboo's NormalizedCollection. Mirrors `parsePostman.js`
 * on purpose — same folder ids, same one-counter DFS `sort_order`, same
 * collection scripts on BOTH the collection and its `settings`, and bodies go
 * through the SAME `mapPostmanBody` (so COL-01 — an imported file path is a
 * hint, never a capability — holds for Bruno and Hoppscotch multipart rows
 * without a second copy of that rule).
 */
import {
  mapPostmanBody,
  normalizeMethod,
  variablesToCollectionSettings,
} from '../normalize';
import { defaultCollectionAuth, defaultRequestAuth } from '../../requestAuth';
import { generateFolderId } from '../../collectionFolders';
import { scriptsOf } from './ir';

const SCRIPT_PATTERNS = {
  bru: [
    [/\brequire\s*\(/, 'require()'],
    [/\bbru\.runRequest\b/, 'bru.runRequest()'],
    [/\bbru\.cookies\b/, 'bru.cookies'],
    [/\breq\.setUrl\b/, 'req.setUrl()'],
  ],
  pw: [[/\bhopp\./, 'the hopp.* API']],
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function newStats() {
  return {
    unsupportedTypes: {},
    vars: 0,
    assertions: 0,
    examples: 0,
    unresolvedPathParams: 0,
    headersCopied: false,
    scriptPatterns: {},
  };
}

/**
 * Bruno/Hoppscotch auth (IR) → Apiboo auth for one scope, or null = "not set" (folders only).
 *
 * The meaning of `none` on a FOLDER differs by source, and getting it wrong leaks a token:
 *  - Bruno: a folder's auth is written only when mode !== 'none', so `none` means "not set here"
 *    (inherit upward).
 *  - Hoppscotch (`dialect: 'pw'`): the nearest parent whose authType !== 'inherit' wins —
 *    INCLUDING `none` (and an inactive auth, which parseHoppscotch reads as none). So a folder
 *    set to None must STOP inheritance: it becomes an explicit `noauth`, which
 *    resolveEffectiveAuthChain honours as a stop (requestAuth.js).
 */
export function toApibooAuth(auth, context, warnings, dialect = 'bru') {
  const base = () => (context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth());
  if (auth && auth.mode === 'none' && context === 'folder' && dialect === 'pw') {
    return { ...defaultRequestAuth(), type: 'noauth' };
  }
  // On a folder or collection, `none`/`inherit`/absent all mean "not set here"
  // (Bruno writes a folder's auth only when mode !== 'none'). On a REQUEST,
  // `none` is an explicit "send no auth".
  if (!auth || auth.mode === 'inherit' || (auth.mode === 'none' && context !== 'request')) {
    return context === 'folder' ? null : base();
  }
  switch (auth.mode) {
    case 'none':
      return { ...defaultRequestAuth(), type: 'noauth' };
    case 'bearer':
      return { ...base(), type: 'bearer', bearer: auth.token || '' };
    case 'basic':
      return { ...base(), type: 'basic', basic: { username: auth.username || '', password: auth.password || '' } };
    case 'apikey':
      return { ...base(), type: 'apikey', apikey: { key: auth.key || '', value: auth.value || '', in: auth.in === 'query' ? 'query' : 'header' } };
    case 'awsv4':
      return {
        ...base(),
        type: 'awssigv4',
        awssigv4: {
          accessKeyId: auth.accessKeyId || '',
          secretAccessKey: auth.secretAccessKey || '',
          sessionToken: auth.sessionToken || '',
          region: auth.region || '',
          service: auth.service || '',
        },
      };
    case 'oauth2': {
      const o = auth.oauth2 || {};
      let grantType = 'manual';
      if (o.grant === 'authorization_code') {
        grantType = 'authorization_code_pkce';
        if (!o.pkce) {
          warnings.push('An OAuth 2.0 authorization-code setup without PKCE was imported as authorization code with PKCE; check that your provider accepts PKCE.');
        }
      } else if (o.grant === 'client_credentials') {
        grantType = 'client_credentials';
      } else {
        warnings.push(`OAuth 2.0 "${o.grant || 'unknown'}" flow is not supported; imported as a manual token, paste one after import.`);
      }
      return {
        ...base(),
        type: 'oauth2',
        oauth2: {
          ...base().oauth2,
          grantType,
          authUrl: o.authUrl || '',
          tokenUrl: o.tokenUrl || '',
          clientId: o.clientId || '',
          clientSecret: o.clientSecret || '',
          scope: o.scope || '',
          redirectUri: o.callbackUrl || '',
          accessToken: o.accessToken || '',
        },
      };
    }
    default:
      // Same message shape as mapPostmanAuth.
      warnings.push(`Unsupported ${context} auth type: ${auth.unsupportedName || auth.mode || 'unknown'}`);
      return context === 'folder' ? null : base();
  }
}

/** IR body → the Postman body shape `mapPostmanBody` already knows. */
function toPostmanBody(body) {
  const b = body || { mode: 'none' };
  switch (b.mode) {
    case 'json':
      return { mode: 'raw', raw: b.text || '', options: { raw: { language: 'json' } } };
    case 'xml':
      return { mode: 'raw', raw: b.text || '', options: { raw: { language: 'xml' } } };
    case 'text':
    case 'sparql':
      return { mode: 'raw', raw: b.text || '', options: { raw: { language: 'text' } } };
    case 'urlencoded':
      return { mode: 'urlencoded', urlencoded: (b.rows || []).map((r) => ({ key: r.key, value: r.value, disabled: !r.enabled })) };
    case 'multipart':
      return {
        mode: 'formdata',
        formdata: (b.rows || []).map((r) => ({
          key: r.key,
          value: r.value,
          type: r.type === 'file' ? 'file' : 'text',
          src: r.type === 'file' ? r.src || '' : undefined,
          disabled: !r.enabled,
        })),
      };
    case 'graphql':
      return { mode: 'graphql', graphql: { query: b.graphql?.query || '', variables: b.graphql?.variables || '' } };
    case 'file':
      return { mode: 'file' };
    default:
      return null;
  }
}

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Apiboo has no path-parameter model: fill `/:name` segments, or keep and count them. */
function applyPathParams(url, pathParams, stats) {
  let out = url;
  for (const p of pathParams || []) {
    if (!p.key) continue;
    const re = new RegExp(`/:${escapeRe(p.key)}(?=[/?#]|$)`, 'g');
    if (!re.test(out)) continue;
    if (p.enabled && p.value !== '') out = out.replace(re, `/${p.value}`);
    else stats.unresolvedPathParams += 1;
  }
  return out;
}

/** Nearer scope wins; the request's own header of the same name wins over all. */
function mergeHeaders(own, inherited) {
  const have = new Set(own.map((h) => h.key.toLowerCase()));
  return [...own, ...inherited.filter((h) => !have.has(h.key.toLowerCase()))];
}

function scopeHeaders(parent, own) {
  const names = new Set(own.map((h) => h.key.toLowerCase()));
  return [...own, ...parent.filter((h) => !names.has(h.key.toLowerCase()))];
}

function scanScripts(texts, dialect, stats) {
  for (const [re, label] of SCRIPT_PATTERNS[dialect] || []) {
    if (texts.some((t) => re.test(t))) stats.scriptPatterns[label] = (stats.scriptPatterns[label] || 0) + 1;
  }
}

function withScripts(target, scripts, dialect, stats) {
  const pre = scripts?.pre || '';
  const test = [scripts?.tests, scripts?.post].filter(Boolean).join('\n\n');
  if (pre) target.preRequestScript = pre;
  if (test) target.testScript = test;
  if (pre || test) target.scriptDialect = dialect;
  if (stats) scanScripts(scriptsOf(scripts), dialect, stats);
  return target;
}

/**
 * One IR request → NormalizedRequest (also used for a single `.bru` / `.yml`
 * file). `inheritedHeaders` are the collection+folder headers to copy down.
 */
export function irRequestToNormalized(node, { folderId = null, sortOrder = 0, inheritedHeaders = [], dialect = 'bru', warnings = [], stats = newStats() } = {}) {
  const { body, body_type, form_data_fields } = mapPostmanBody(toPostmanBody(node.body), warnings);
  const settings =
    (body_type === 'formdata' || body_type === 'form') && form_data_fields?.length ? { form_data_fields } : {};
  const headers = mergeHeaders(node.headers || [], inheritedHeaders);
  if (headers.length > (node.headers || []).length) stats.headersCopied = true;
  const counts = node.counts || {};
  stats.vars += counts.vars || 0;
  stats.assertions += counts.assertions || 0;
  stats.examples += counts.examples || 0;

  const mapped = {
    name: node.name || 'Request',
    description: node.docs || '',
    method: normalizeMethod(node.method),
    url: applyPathParams(node.url || '', node.pathParams, stats),
    headers,
    params: node.query || [],
    cookies: [],
    body,
    body_type,
    auth: toApibooAuth(node.auth, 'request', warnings),
    settings,
    folder_id: folderId,
    sort_order: sortOrder,
  };
  return withScripts(mapped, node.scripts, dialect, stats);
}

const bySeq = (a, b) => {
  const sa = Number.isFinite(a.seq) ? a.seq : Infinity;
  const sb = Number.isFinite(b.seq) ? b.seq : Infinity;
  if (sa !== sb) return sa - sb;
  return String(a.name || '').localeCompare(String(b.name || ''));
};

function walk(children, parentId, parentHeaders, ctx) {
  for (const node of [...(children || [])].sort(bySeq)) {
    if (node.kind === 'folder') {
      const id = generateFolderId();
      const folder = { id, name: node.name || 'Folder', parent_id: parentId, sort_order: ctx.order.value++ };
      withScripts(folder, node.defaults?.scripts, ctx.dialect, ctx.stats);
      const auth = toApibooAuth(node.defaults?.auth, 'folder', ctx.warnings, ctx.dialect);
      if (auth) folder.auth = auth;
      // Disabled (`~`) rows are kept as `enabled: false`, like collection vars.
      const vars = (node.defaults?.vars || []).filter((v) => v.key);
      if (vars.length) {
        folder.variables = vars.map((v) => ({ key: v.key, value: v.value, enabled: v.enabled !== false }));
      }
      ctx.folders.push(folder);
      walk(node.children, id, scopeHeaders(parentHeaders, node.defaults?.headers || []), ctx);
      continue;
    }
    if (node.reqType === 'unsupported') {
      const t = node.typeName || 'unknown';
      ctx.stats.unsupportedTypes[t] = (ctx.stats.unsupportedTypes[t] || 0) + 1;
      continue;
    }
    ctx.requests.push(
      irRequestToNormalized(node, {
        folderId: parentId,
        sortOrder: ctx.order.value++,
        inheritedHeaders: parentHeaders,
        dialect: ctx.dialect,
        warnings: ctx.warnings,
        stats: ctx.stats,
      })
    );
  }
}

function flushStats(stats, warnings, sourceLabel) {
  for (const [type, n] of Object.entries(stats.unsupportedTypes)) {
    warnings.push(`${plural(n, `${type} request was`, `${type} requests were`)} skipped (Apiboo imports HTTP and GraphQL requests).`);
  }
  if (stats.headersCopied) {
    warnings.push(`${sourceLabel} collection/folder headers were copied onto every request below them (Apiboo has no collection- or folder-level headers).`);
  }
  if (stats.unresolvedPathParams) {
    warnings.push(`${plural(stats.unresolvedPathParams, 'path parameter has', 'path parameters have')} no value; the :name placeholder was left in the URL.`);
  }
  if (stats.vars) warnings.push(`${plural(stats.vars, 'request variable was', 'request variables were')} not imported (Apiboo has no request-scoped variables).`);
  if (stats.assertions) warnings.push(`${plural(stats.assertions, 'assertion was', 'assertions were')} not imported; rewrite them as test() checks.`);
  if (stats.examples) warnings.push(`${plural(stats.examples, 'saved example was', 'saved examples were')} not imported.`);
  for (const [label, n] of Object.entries(stats.scriptPatterns)) {
    warnings.push(`${plural(n, 'script uses', 'scripts use')} ${label}, which Apiboo does not support; that script will stop with an error.`);
  }
}

/**
 * @param {object} ir BrunoCollectionIR (or the Hoppscotch equivalent)
 * @param {string[]} warnings
 * @param {{ dialect?: 'bru'|'pw', sourceLabel?: string }} [opts]
 * @returns {import('../types').NormalizedCollection}
 */
export function irToNormalized(ir, warnings, { dialect = 'bru', sourceLabel = 'Bruno' } = {}) {
  const ctx = { folders: [], requests: [], order: { value: 0 }, dialect, warnings, stats: newStats() };
  walk(ir.children, null, ir.defaults?.headers || [], ctx);

  const vars = (ir.defaults?.vars || []).map((v) => ({ key: v.key, value: v.value, disabled: !v.enabled }));
  const settings = {
    ...variablesToCollectionSettings(vars),
    folders: ctx.folders,
    collection_auth: toApibooAuth(ir.defaults?.auth, 'collection', warnings),
  };
  const normalized = {
    name: ir.name || 'Imported Collection',
    description: ir.docs || '',
    settings,
    requests: ctx.requests,
  };
  // Also in `settings`: the only part both import paths persist, and where
  // Run collection reads it (utils/scriptChain) — same as parsePostman.js.
  withScripts(normalized, ir.defaults?.scripts, dialect, ctx.stats);
  withScripts(settings, ir.defaults?.scripts, dialect, null);
  flushStats(ctx.stats, warnings, sourceLabel);
  return normalized;
}

/** O7: a secret's value is never in an export; say how many need filling in. */
export function secretEnvWarning(environments, warnings) {
  const n = (environments || []).reduce((acc, e) => acc + (e.variables || []).filter((v) => v.secret).length, 0);
  if (n) warnings.push(`${plural(n, 'secret value was', 'secret values were')} not in the export; fill ${n === 1 ? 'it' : 'them'} in after import.`);
}
